import { hidesBillingUi, isUnlimited, PLAN_IDS, PLANS } from '@qr/shared/plans';
import type { Member, Role } from '@qr/shared/types';
import { Button, Callout, Field, Spinner } from '@qr/ui';
import { collection, onSnapshot } from 'firebase/firestore';
import { AnimatePresence } from 'motion/react';
import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Cajon } from '../components/Cajon.js';
import { api, messageFromError } from '../lib/api.js';
import { useAuth } from '../lib/auth.js';
import { db } from '../lib/firebase.js';
import { useCan, useOrg } from '../lib/org.js';
import { useConfirmar } from '../lib/dialogos.js';

const ROLE_LABELS: Record<Role, string> = {
  owner: 'Propietario',
  admin: 'Administrador',
  editor: 'Editor',
  viewer: 'Lectura',
};

const ROLE_HELP: Record<Role, string> = {
  owner: 'Control total de la cuenta.',
  admin: 'Gestiona códigos y equipo.',
  editor: 'Crea y edita códigos.',
  viewer: 'Solo consulta.',
};

/** Los roles que se pueden asignar. `owner` no está: solo hay uno y no se cede aquí. */
const ROLES_ASIGNABLES = ['viewer', 'editor', 'admin'] as const;
type RolAsignable = (typeof ROLES_ASIGNABLES)[number];

export function Team() {
  const org = useOrg();
  const { user } = useAuth();
  const canManage = useCan('admin');

  const [members, setMembers] = useState<Member[] | null>(null);
  const [invites, setInvites] = useState<Invitacion[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [invitando, setInvitando] = useState(false);

  /*
   * Las invitaciones no llegan por `onSnapshot` como los miembros: la
   * coleccion no se puede leer desde el cliente, asi que se piden al servidor
   * y se vuelven a pedir despues de cada cambio.
   */
  const [recarga, setRecarga] = useState(0);
  const recargarInvitaciones = useCallback(() => setRecarga((n) => n + 1), []);

  useEffect(() => {
    if (!org.orgId || !canManage) return;
    // Si se cambia de espacio mientras la peticion viaja, la respuesta vieja
    // llegaria despues y pintaria las invitaciones del espacio anterior.
    let vigente = true;
    api
      .listInvites({ orgId: org.orgId })
      .then(({ invitaciones }) => {
        if (vigente) setInvites(invitaciones);
      })
      .catch((caught: unknown) => {
        console.error('No se pudieron cargar las invitaciones', caught);
        if (vigente) setInvites([]);
      });
    return () => {
      vigente = false;
    };
  }, [org.orgId, canManage, recarga]);

  useEffect(() => {
    if (!org.orgId) return;
    return onSnapshot(
      collection(db, `orgs/${org.orgId}/members`),
      (snap) => {
        // El uid va despues del spread: el documento ya lo lleva como campo y
        // el id de la ruta es el dato autoritativo si alguno difiriera.
        setMembers(snap.docs.map((d) => ({ ...(d.data() as Member), uid: d.id })));
        setLoadError(null);
      },
      (caught) => {
        // Un fallo silencioso aqui se ve como una lista vacia, que es
        // indistinguible de 'no hay nadie' y no deja diagnosticar nada.
        console.error('No se pudo cargar el equipo', caught);
        setLoadError(messageFromError(caught));
        setMembers([]);
      },
    );
  }, [org.orgId]);

  /*
   * Una invitacion sin aceptar ya ocupa plaza, igual que en el servidor. Si el
   * panel contara solo a quien esta dentro, dejaria invitar hasta pasarse y el
   * error saltaria al pulsar, que es el peor momento para enterarse.
   */
  const pendientesVivas = (invites ?? []).filter((i) => !i.caducada).length;
  const plazasUsadas = (members?.length ?? 0) + pendientesVivas;
  const atLimit =
    members !== null &&
    !isUnlimited(org.limits.teamMembers) &&
    plazasUsadas >= org.limits.teamMembers;

  /*
   * Quedarse sin plazas y no tener equipo no son lo mismo.
   *
   * Con una sola plaza nunca hubo equipo: esa cuenta eres tu y nadie mas. Ahi
   * decirle "no quedan plazas, cancela alguna" es regañar a alguien por algo
   * que no ha hecho, y ademas pedirle que cancele invitaciones que no existen.
   * Lo que toca es contarle para que sirve traer gente y que plan lo trae.
   *
   * Con dos o mas si hubo equipo y el aviso de siempre es correcto: se lleno,
   * y liberar una plaza es una salida real.
   */
  const sinEquipo = org.limits.teamMembers <= 1 && !org.isComped;
  const planConEquipo = PLAN_IDS
    .filter((id) => !hidesBillingUi(id))
    .map((id) => PLANS[id])
    .find((plan) => plan.limits.teamMembers > 1);

  return (
    <div className="flex flex-col">
      <header className="flex flex-wrap items-center gap-3 border-b border-line px-4 py-3 lg:px-6">
        <h1 className="m-0 text-lg font-semibold tracking-tight">Equipo</h1>
        {members ? (
          <span className="rounded-full border border-line px-2 py-0.5 text-[11.5px] text-ink-3 tabular-nums">
            {/* Con el tope al lado: un "3" suelto no dice si queda sitio. */}
            {plazasUsadas}
            {isUnlimited(org.limits.teamMembers) ? '' : ` / ${org.limits.teamMembers}`}
          </span>
        ) : null}

        {canManage && !sinEquipo ? (
          <Button
            className="ms-auto"
            onClick={() => setInvitando(true)}
            disabled={atLimit}
          >
            <svg width="13" height="13" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <path
                d="M8 3v10M3 8h10"
                stroke="currentColor"
                strokeWidth="1.9"
                strokeLinecap="round"
              />
            </svg>
            Invitar a alguien
          </Button>
        ) : null}
      </header>

      <div className="flex max-w-[56rem] flex-col gap-5 px-4 py-5 lg:px-6">
        {sinEquipo ? (
          <Callout tone="info" title="Trabaja con más gente">
            Ahora mismo llevas los códigos tú solo. Con un equipo puedes dar
            entrada a quien se encargue de cambiar los destinos, revisar los
            escaneos o preparar los códigos de una campaña, cada uno con su
            propia cuenta y sin compartir tu contraseña.
            {planConEquipo ? (
              <>
                {' '}
                Entra en el plan {planConEquipo.name}, que admite{' '}
                {planConEquipo.limits.teamMembers} personas.
              </>
            ) : null}{' '}
            <a
              href="https://apuntaqr.com/#precios"
              target="_blank"
              rel="noreferrer"
            >
              Ver los planes
            </a>
          </Callout>
        ) : atLimit && !org.isComped ? (
          <Callout tone="warning" title="No quedan plazas">
            Tu plan permite {org.limits.teamMembers} personas
            {pendientesVivas > 0 ? (
              <>
                , y {pendientesVivas}{' '}
                {pendientesVivas === 1
                  ? 'invitación sin aceptar ya ocupa una'
                  : 'invitaciones sin aceptar ya ocupan plaza'}
              </>
            ) : null}
            . Libera alguna o cambia de plan.
          </Callout>
        ) : null}

        {loadError ? <Callout tone="danger">{loadError}</Callout> : null}

        {members === null ? (
          <div className="center">
            <Spinner label="Cargando el equipo" />
          </div>
        ) : (
          <ul className="flex list-none flex-col gap-2 p-0">
            {members.map((member) => (
              <Fila
                key={member.uid}
                member={member}
                esYo={member.uid === user?.uid}
                puedeGestionar={canManage}
                orgId={org.orgId}
              />
            ))}
          </ul>
        )}

        {canManage && invites && invites.length > 0 ? (
          <Pendientes
            invites={invites}
            orgId={org.orgId}
            onCambio={recargarInvitaciones}
          />
        ) : null}

        <Salir />
      </div>

      <AnimatePresence>
        {invitando ? (
          <Cajon titulo="Invitar a alguien" onCerrar={() => setInvitando(false)}>
            <InviteForm orgId={org.orgId} onInvitado={recargarInvitaciones} />
          </Cajon>
        ) : null}
      </AnimatePresence>
    </div>
  );
}

function Fila({
  member,
  esYo,
  puedeGestionar,
  orgId,
}: {
  readonly member: Member;
  readonly esYo: boolean;
  readonly puedeGestionar: boolean;
  readonly orgId: string | null;
}) {
  const confirmar = useConfirmar();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Al propietario no se le toca el rol, y nadie se rebaja a si mismo: eso
  // ultimo dejaria una cuenta sin quien la administre si es el unico admin.
  const editable = puedeGestionar && member.role !== 'owner' && !esYo;

  const inicial = (member.displayName || member.email || '?').slice(0, 1).toUpperCase();

  return (
    <li className="flex flex-wrap items-center gap-3 rounded-lg border border-line bg-surface px-4 py-3">
      <span
        className="grid size-9 shrink-0 place-items-center rounded-full bg-brand-soft text-[13px] font-semibold text-brand-deep"
        aria-hidden="true"
      >
        {inicial}
      </span>

      <div className="min-w-0 flex-1">
        <strong className="block truncate text-[14px]">
          {member.displayName || member.email}
          {esYo ? <span className="ms-2 text-[11.5px] text-ink-3">(tú)</span> : null}
        </strong>
        {member.displayName ? (
          <span className="block truncate text-[12.5px] text-ink-3">{member.email}</span>
        ) : null}
      </div>

      {editable ? (
        <label className="flex flex-col gap-0.5">
          <span className="sr-only">Permisos de {member.email}</span>
          <select
            id={`rol-${member.uid}`}
            value={member.role}
            disabled={busy}
            className="rounded-sm border border-line bg-surface px-2.5 py-1.5 text-[12.5px] text-ink"
            onChange={async (e) => {
              if (!orgId) return;
              setError(null);
              setBusy(true);
              try {
                await api.updateMemberRole({
                  orgId,
                  uid: member.uid,
                  role: e.target.value as RolAsignable,
                });
              } catch (caught) {
                setError(messageFromError(caught));
              } finally {
                setBusy(false);
              }
            }}
          >
            {ROLES_ASIGNABLES.map((r) => (
              <option key={r} value={r}>
                {ROLE_LABELS[r]}
              </option>
            ))}
          </select>
        </label>
      ) : (
        <span className="flex flex-col items-end text-end">
          <span className="badge">{ROLE_LABELS[member.role]}</span>
          <span className="text-[11.5px] text-ink-3">{ROLE_HELP[member.role]}</span>
        </span>
      )}

      {editable ? (
        <Button
          size="sm"
          variant="ghost"
          disabled={busy}
          onClick={async () => {
            const confirmed = await confirmar({
              titulo: 'Quitar del equipo',
              mensaje: `${member.email} dejará de tener acceso a este negocio.`,
              aceptar: 'Quitar',
              grave: true,
            });
            if (!confirmed || !orgId) return;
            await api.removeMember({ orgId, uid: member.uid });
          }}
        >
          Quitar
        </Button>
      ) : null}

      {error ? (
        <p className="w-full text-[12.5px] text-bad" role="alert">
          {error}
        </p>
      ) : null}
    </li>
  );
}

/**
 * Salir de un espacio ajeno.
 *
 * Solo aparece donde tiene sentido: en el espacio propio no se ofrece, porque
 * el propietario no puede abandonarse a sí mismo sin dejar la cuenta sin dueño.
 *
 * Va replegado y al final. Es irreversible sin una invitación nueva, así que no
 * debe estar al alcance de un clic distraído.
 */
function Salir() {
  const confirmar = useConfirmar();
  const org = useOrg();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (org.role === 'owner' || !org.orgId) return null;

  return (
    <details className="mt-2 rounded border border-line px-4 py-3 text-[13px]">
      <summary className="cursor-pointer text-ink-2">Salir de este negocio</summary>
      <div className="flex flex-col items-start gap-3 pt-3">
        <p className="m-0 text-ink-2">
          Dejarás de ver los códigos y las estadísticas de{' '}
          <strong>{org.name}</strong>. Para volver a entrar necesitarás una invitación
          nueva.
        </p>
        {error ? <Callout tone="danger">{error}</Callout> : null}
        <Button
          variant="danger"
          size="sm"
          loading={busy}
          onClick={async () => {
            const confirmed = await confirmar({
              titulo: `Salir de "${org.name}"`,
              mensaje:
                'Perderás el acceso a sus códigos. Para volver a entrar tendrán que invitarte otra vez.',
              aceptar: 'Salir',
              grave: true,
            });
            if (!confirmed || !org.orgId) return;
            setError(null);
            setBusy(true);
            try {
              await api.leaveOrg({ orgId: org.orgId });
            } catch (caught) {
              setError(messageFromError(caught));
            } finally {
              setBusy(false);
            }
          }}
        >
          Salir del negocio
        </Button>
      </div>
    </details>
  );
}

function InviteForm({
  orgId,
  onInvitado,
}: {
  readonly orgId: string | null;
  readonly onInvitado: () => void;
}) {
  const [email, setEmail] = useState('');
  const [destino, setDestino] = useState('');
  const [role, setRole] = useState<RolAsignable>('editor');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [inviteUrl, setInviteUrl] = useState<string | null>(null);
  const [correoEnviado, setCorreoEnviado] = useState(false);
  const [copiado, setCopiado] = useState(false);

  useEffect(() => {
    if (!copiado) return;
    const t = setTimeout(() => setCopiado(false), 1800);
    return () => clearTimeout(t);
  }, [copiado]);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (!orgId) return;
    setError(null);
    setBusy(true);
    try {
      const result = await api.inviteMember({ orgId, email: email.trim(), role });
      setInviteUrl(result.acceptUrl);
      setCorreoEnviado(result.correoEnviado);
      setDestino(email.trim().toLowerCase());
      setEmail('');
      onInvitado();
    } catch (caught) {
      setError(messageFromError(caught));
    } finally {
      setBusy(false);
    }
  }

  if (inviteUrl) {
    return (
      <div className="flex flex-col gap-4">
{/*
          Dos mensajes distintos, porque son dos situaciones distintas: o ya
          está hecho y el enlace es un por si acaso, o no está hecho y el
          enlace es la única forma de que esa persona entre. Decir lo mismo en
          los dos casos dejaría a alguien esperando un correo que no salió.
        */}
        {correoEnviado ? (
          <Callout tone="success" title="Invitación enviada">
            Le hemos mandado un correo a <strong>{destino}</strong> con el enlace
            para entrar. Caduca en 7 días y{' '}
            <strong>solo funciona con esa dirección</strong>. Aquí tienes el mismo
            enlace por si prefieres pasárselo tú.
          </Callout>
        ) : (
          <Callout tone="info" title="Invitación creada">
            Envíale este enlace por donde quieras —correo, WhatsApp—. Caduca en 7
            días y <strong>solo funciona con el correo al que va dirigido</strong>,
            así que reenviarlo a otra persona no le da acceso. Si lo pierdes,
            puedes generar uno nuevo desde la lista de invitaciones pendientes.
          </Callout>
        )}

        <p className="m-0 overflow-x-auto rounded-sm border border-line bg-ground px-3 py-2.5 font-mono text-[12px]">
          {inviteUrl}
        </p>

        <div className="flex gap-2">
          <Button
            variant="secondary"
            onClick={async () => {
              try {
                await navigator.clipboard.writeText(inviteUrl);
                setCopiado(true);
              } catch {
                // Sin permiso de portapapeles el enlace sigue a la vista.
              }
            }}
          >
            {copiado ? 'Copiado' : 'Copiar enlace'}
          </Button>
          <Button onClick={() => setInviteUrl(null)}>Invitar a otra persona</Button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="stack-sm" noValidate>
      <Field
        label="Correo electrónico"
        type="email"
        value={email}
        onChange={(event) => setEmail(event.target.value)}
        placeholder="companero@empresa.com"
        required
        error={error}
        hint="La invitación queda atada a este correo."
      />

      <div className="qr-field">
        <label className="qr-field__label" htmlFor="invite-role">
          Permisos
        </label>
        <select
          id="invite-role"
          className="qr-field__input"
          value={role}
          onChange={(event) => setRole(event.target.value as RolAsignable)}
        >
          <option value="viewer">Lectura — solo consulta</option>
          <option value="editor">Editor — crea y edita códigos</option>
          <option value="admin">Administrador — gestiona códigos y equipo</option>
        </select>
        <p className="qr-field__hint">Podrás cambiarlo después desde la lista.</p>
      </div>

      <div>
        <Button type="submit" loading={busy} disabled={email.trim() === ''}>
          Crear invitación
        </Button>
      </div>
    </form>
  );
}

type Invitacion = {
  readonly inviteId: string;
  readonly email: string;
  readonly role: Role;
  readonly expiresAt: string;
  readonly caducada: boolean;
};

/** "Caduca en 5 días", que es lo que se quiere saber, y no una fecha. */
function cuantoQueda(iso: string): string {
  const dias = Math.ceil((new Date(iso).getTime() - Date.now()) / 86_400_000);
  if (dias <= 0) return 'Caducada';
  if (dias === 1) return 'Caduca mañana';
  return `Caduca en ${dias} días`;
}

/**
 * Invitaciones enviadas que nadie ha aceptado todavía.
 *
 * Es la mitad que faltaba: hasta ahora el enlace se veía una vez y desaparecía.
 * Quien invitaba no podía saber a quién había invitado, ni recuperar el enlace
 * si se le cerró la ventana, ni cancelar al que se equivocó de correo — y esa
 * invitación seguía ocupando una plaza del plan.
 */
function Pendientes({
  invites,
  orgId,
  onCambio,
}: {
  readonly invites: readonly Invitacion[];
  readonly orgId: string | null;
  readonly onCambio: () => void;
}) {
  const confirmar = useConfirmar();
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [enlace, setEnlace] = useState<string | null>(null);
  const [reenviadoPorCorreo, setReenviadoPorCorreo] = useState(false);
  const [copiado, setCopiado] = useState(false);

  useEffect(() => {
    if (!copiado) return;
    const t = setTimeout(() => setCopiado(false), 1800);
    return () => clearTimeout(t);
  }, [copiado]);

  return (
    <section className="flex flex-col gap-2">
      <h2 className="m-0 text-[13px] font-semibold uppercase tracking-wide text-ink-3">
        Invitaciones sin aceptar
      </h2>

      {error ? <Callout tone="danger">{error}</Callout> : null}

      {enlace ? (
        <div className="flex flex-col gap-2 rounded-lg border border-brand-soft bg-brand-soft/30 p-3">
          <p className="m-0 text-[12.5px] text-ink-2">
            {reenviadoPorCorreo
              ? 'Le hemos mandado el enlace nuevo por correo. El anterior ha dejado de funcionar.'
              : 'Enlace nuevo. El anterior ha dejado de funcionar.'}
          </p>
          <p className="m-0 overflow-x-auto rounded-sm border border-line bg-ground px-3 py-2 font-mono text-[12px]">
            {enlace}
          </p>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="secondary"
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(enlace);
                  setCopiado(true);
                } catch {
                  // Sin permiso de portapapeles el enlace sigue a la vista.
                }
              }}
            >
              {copiado ? 'Copiado' : 'Copiar enlace'}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setEnlace(null)}>
              Listo
            </Button>
          </div>
        </div>
      ) : null}

      <ul className="flex list-none flex-col gap-2 p-0">
        {invites.map((invite) => (
          <li
            key={invite.inviteId}
            className="flex flex-wrap items-center gap-3 rounded-lg border border-dashed border-line px-4 py-3"
          >
            <div className="min-w-0 flex-1">
              <strong className="block truncate text-[14px] font-medium">
                {invite.email}
              </strong>
              <span
                className={`block text-[12px] ${invite.caducada ? 'text-bad' : 'text-ink-3'}`}
              >
                {ROLE_LABELS[invite.role]} · {cuantoQueda(invite.expiresAt)}
              </span>
            </div>

            <Button
              size="sm"
              variant="secondary"
              disabled={busyId === invite.inviteId}
              onClick={async () => {
                if (!orgId) return;
                setError(null);
                setBusyId(invite.inviteId);
                try {
                  const r = await api.resendInvite({
                    orgId,
                    inviteId: invite.inviteId,
                  });
                  setEnlace(r.acceptUrl);
                  setReenviadoPorCorreo(r.correoEnviado);
                  onCambio();
                } catch (caught) {
                  setError(messageFromError(caught));
                } finally {
                  setBusyId(null);
                }
              }}
            >
              {invite.caducada ? 'Renovar' : 'Reenviar'}
            </Button>

            <Button
              size="sm"
              variant="ghost"
              disabled={busyId === invite.inviteId}
              onClick={async () => {
                if (!orgId) return;
                const ok = await confirmar({
                  titulo: 'Cancelar la invitación',
                  mensaje: `El enlace que recibió ${invite.email} dejará de funcionar.`,
                  aceptar: 'Cancelar la invitación',
                  cancelar: 'Dejarla activa',
                  grave: true,
                });
                if (!ok) return;
                setError(null);
                setBusyId(invite.inviteId);
                try {
                  await api.revokeInvite({ orgId, inviteId: invite.inviteId });
                  onCambio();
                } catch (caught) {
                  setError(messageFromError(caught));
                } finally {
                  setBusyId(null);
                }
              }}
            >
              Cancelar
            </Button>
          </li>
        ))}
      </ul>
    </section>
  );
}
